import React, { useState, useGlobal } from "reactn";
import PropTypes from "prop-types";

// components
import NavigationLink from "./navLink/NavigationLink";
import Logo from "./logo/Logo";
import { SideNavStyled, LineSeparator } from "./SideNav.styled";
import SwitchProfileButton from "../SwitchProfile/SwitchProfileButton";
import { SideNavTopSection } from "./SideNavTopSection.styled";

// icons
import InboxIcon from "./images/inbox.svg";
import InboxIconHover from "./images/inbox-hover.svg";
import InboxBusinessIcon from "./images/inbox-business.svg";
import InboxBusinessIconHover from "./images/inbox-business-hover.svg";
import TransactionIcon from "./images/transaction-history.svg";
import TransactionIconHover from "./images/transaction-history-hover.svg";
import TransactionBusinessIcon from "./images/transaction-history-business.svg";
import TransactionBusinessIconHover from "./images/transaction-history-business-hover.svg";
import DeliveryIcon from "./images/delivery.svg";
import DeliveryIconHover from "./images/delivery-hover.svg";
import DeliveryBusinessIcon from "./images/delivery-business.svg";
import DeliveryBusinessIconHover from "./images/delivery-business-hover.svg";
import ParcelIcon from "./images/parcel.svg";
import ParcelIconHover from "./images/parcel-hover.svg";
import ParcelBusinessIcon from "./images/parcel-business.svg";
import ParcelBusinessIconHover from "./images/parcel-business-hover.svg";
import ProfileIcon from "./images/profile.svg";
import ProfileIconHover from "./images/profile-hover.svg";
import ProfileBusinessIcon from "./images/profile-business.svg";
import ProfileBusinessIconHover from "./images/profile-business-hover.svg";
import AddressIcon from "./images/address.svg";
import AddressIconHover from "./images/address-hover.svg";
import AddressBusinessIcon from "./images/address-business.svg";
import AddressBusinessIconHover from "./images/address-business-hover.svg";
import ShopIcon from "./images/shop.svg";
import ShopIconHover from "./images/shop-hover.svg";
import ShopBusinessIcon from "./images/shop-business.svg";
import ShopBusinessIconHover from "./images/shop-business-hover.svg";
import DirectMailIcon from "./images/direct-mail.svg";
import DirectMailIconHover from "./images/direct-mail-hover.svg";

// styles
import { ThemeProvider } from 'styled-components'
import ThemeProviderStyled from '../../theme/ThemeProvider.styled'

// hooks
import CollapseButton from "./CollapseButton/CollapseButton";
import AppHook from "./hooks/appHook";

// Config
import ExternalLink from "./externalLink/ExternalLink";

const EXT_URLS = [
    "click-and-post",
    "shop",
    "my-deliveries",
    "direct-mail"
]

const SideNav = ({ business, history, isOverview }) => {
    // global state
    const [isCollapsed, setIsCollapsed] = useGlobal("isCollapsed");

    // local state
    const [sideNavIndex, setSideNavIndex] = useGlobal("sideNavIndex");
    const [submenuIndex, setSubmenuIndex] = useGlobal("submenuIndex");
    const [sideMenuItemHovered, setSideMenuItemHovered] = useState(-1);

    // hooks
    const { handleSlash } = AppHook();

    const sideNavData = [
        {
            title: "Overview",
            link: `${sideNavIndex === 1 ? "/myAccount" : "/"}`,
            cssClassNames: `${sideNavIndex === 1 ? "link-outside-of-app" : ""}`,
            menuIcon: InboxIcon,
            menuIconHover: InboxIconHover,
            businessMenuIcon: InboxBusinessIcon,
            businessMenuIconHover: InboxBusinessIconHover
        },
        {
            title: "My deliveries",
            link:  `${sideNavIndex === 1 ? "/" : "/myAccount/My-deliveries"}`,
            cssClassNames: `${sideNavIndex === 1 ? "" : "link-outside-of-app"}`,
            menuIcon: DeliveryIcon,
            menuIconHover: DeliveryIconHover,
            businessMenuIcon: DeliveryBusinessIcon,
            businessMenuIconHover: DeliveryBusinessIconHover
        },
        {
            title: "Direct mail",
            link:  "/Commerce/Direct-Mail",
            cssClassNames: "",
            businessMenuIcon: DirectMailIcon,
            businessMenuIconHover: DirectMailIconHover
        },
        {
            title: "Transactions",
            link: `${sideNavIndex === 1 ? "/myAccount/Transactions" : "/Transactions"}`,
            cssClassNames: `${sideNavIndex === 1 ? "link-outside-of-app" : ""}`,
            menuIcon: TransactionIcon,
            menuIconHover: TransactionIconHover,
            businessMenuIcon: TransactionBusinessIcon,
            businessMenuIconHover: TransactionBusinessIconHover
        },
        {
            title: "Profile",
            link: `${sideNavIndex === 1 ? "/myAccount/Profile" : "/Profile"}`,
            cssClassNames: `${sideNavIndex === 1 ? "link-outside-of-app" : ""}`,
            menuIcon: ProfileIcon,
            menuIconHover: ProfileIconHover,
            businessMenuIcon: ProfileBusinessIcon,
            businessMenuIconHover: ProfileBusinessIconHover
        },
        {
            title: "Address book",
            link: `${sideNavIndex === 1 ? "/myAccount/Addresses" : "/Addresses"}`,
            cssClassNames: `${sideNavIndex === 1 ? "link-outside-of-app" : ""}`,
            menuIcon: AddressIcon,
            menuIconHover: AddressIconHover,
            businessMenuIcon: AddressBusinessIcon,
            businessMenuIconHover: AddressBusinessIconHover
        },
        {
            title: "Click & Post",
            link: "/Post-Parcels/Click-and-Post",
            cssClassNames: "",
            menuIcon: ParcelIcon,
            menuIconHover: ParcelIconHover,
            businessMenuIcon: ParcelBusinessIcon,
            businessMenuIconHover: ParcelBusinessIconHover
        },
        {
            title: "Shop",
            link: "/Shop",
            cssClassNames: "",
            menuIcon: ShopIcon,
            menuIconHover: ShopIconHover,
            businessMenuIcon: ShopBusinessIcon,
            businessMenuIconHover: ShopBusinessIconHover
        },
    ];

    // check for external urls
    const checkExtUrls = (url, classname) => {
        if (EXT_URLS.some(extUrl => url.toLowerCase().includes(extUrl))) {
            return true;
        }

        if (classname === "link-outside-of-app") {
            return true;
        }

        return false;
    };

    // NOTE: In my-click-and-post app, the sideNavIndex for transactions is set to 3 when the user opens the transactions page form direct link (HomePage.js component)

    const setNavIndexes = (url, i) => {
        if (url.toLowerCase().includes("transactions")) {
            setSideNavIndex(i);
            setSubmenuIndex(5);
        } else if (url.toLowerCase().includes("my-deliveries")) {
            setSideNavIndex(i);
            setSubmenuIndex(i);
        }
        else {
            setSideNavIndex(i);
            setSubmenuIndex(i);
        }
    };

    const setIcon = (item, index) => {
        let icon;

        if (business) {
            icon = sideMenuItemHovered === index ? item.businessMenuIconHover : item.businessMenuIcon;
        } else {
            icon = sideMenuItemHovered === index ? item.menuIconHover : item.menuIcon;
        }

        return icon;
    };

    return (
    <ThemeProvider theme={ThemeProviderStyled}>
        <SideNavStyled collapsed={isCollapsed} business={business}>
            <SideNavTopSection business={business} collapsed={isCollapsed}>
                <Logo />
                <SwitchProfileButton className="switch__profile" collapsed={isCollapsed} history={history} isOverview={isOverview} />
            </SideNavTopSection>
            <ul className="sideNav__links">
                {sideNavData &&
                    sideNavData.map((item, i) => (
                        <React.Fragment key={item.title}>
                            {
                                item.link.toLowerCase().includes("click-and-post") && 
                                    <LineSeparator 
                                        aria-hidden="true" 
                                        business={business} 
                                        collapsed={isCollapsed} 
                                    />
                            }
                            <li className={!business && item.link.toLowerCase().includes("direct-mail") ? "hidden" : ""}>
                                {checkExtUrls(item.link, item.cssClassNames) ? (
                                    <ExternalLink
                                        className={`sidenav-menu-item ${i === sideNavIndex ? "active-link" : ""} gtm-sidenavMACH`}
                                        active={i === sideNavIndex}
                                        to={`${window.location.origin}/${handleSlash(item.link)}`}
                                        target={item.target ? item.target : ""}
                                        text={!isCollapsed ? item.title : ""}
                                        key={item.title}
                                        icon={setIcon(item, i)}
                                        activeIcon={business ? item.businessMenuIconHover : item.menuIconHover}
                                        altText={`${item.title} icon`}
                                        onClick={() => setSideNavIndex(i)}
                                        onMouseEnter={() => setSideMenuItemHovered(i)}
                                        onMouseLeave={() => setSideMenuItemHovered(-1)}
                                    />
                                ) : (
                                    <NavigationLink
                                        className={`sidenav-menu-item ${i === sideNavIndex ? "active-link" : ""} gtm-sidenavMACH`}
                                        active={i === sideNavIndex}
                                        to={`${item.link}`}
                                        target={item.target ? item.target : ""}
                                        text={!isCollapsed ? item.title : ""}
                                        key={item.title}
                                        icon={setIcon(item, i)}
                                        activeIcon={business ? item.businessMenuIconHover : item.menuIconHover}
                                        altText={`${item.title} icon`}
                                        onClick={() => setNavIndexes(item.link, i)}
                                        onMouseEnter={() => setSideMenuItemHovered(i)}
                                        onMouseLeave={() => setSideMenuItemHovered(-1)}
                                    />
                                )}
                            </li>
                        </React.Fragment>
                    ))}
            </ul>
            <div className="collapse__section">
                <CollapseButton className="collapse__button gtm-sideNavMACH" />
            </div>
        </SideNavStyled>
    </ThemeProvider> 
    );
};

export default SideNav;

SideNav.propTypes = {
    business: PropTypes.bool,
    history: PropTypes.object,
    isOverview: PropTypes.bool
};
